import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    HeightRule,
    TableLayoutType,
    ImageRun,
    AlignmentType,
    VerticalAlign,
    HorizontalPositionRelativeFrom,
    VerticalPositionRelativeFrom,
    TextWrappingType,
    HorizontalPositionAlign,
    VerticalPositionAlign
} from "docx";
import { hexToRGBA, FULL_WIDTH, SCALE, fixColor } from "@/utils/wordGenerator/common.js";

const generateFPTemp4HeaderImage = async (headerColor, logoUrl) => {
    return new Promise((resolve) => {
        try {
            const canvas = document.createElement('canvas');
            const WIDTH = 1600;
            const HEIGHT = 120;
            canvas.width = WIDTH;
            canvas.height = HEIGHT;
            const ctx = canvas.getContext('2d');

            // White Background
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            // Gradient
            const grad = ctx.createLinearGradient(0, 0, 0, HEIGHT);
            grad.addColorStop(0, hexToRGBA(headerColor, 0.8));
            grad.addColorStop(1, hexToRGBA(headerColor, 0));
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            if (logoUrl) {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = () => {
                    const logoBoxW = 350;
                    const logoBoxH = HEIGHT;
                    const padding = 15;

                    const drawAreaW = logoBoxW - (padding * 2);
                    const drawAreaH = logoBoxH - (padding * 2);

                    const scale = Math.min(drawAreaW / img.width, drawAreaH / img.height);
                    const w = img.width * scale;
                    const h = img.height * scale;

                    const x = (WIDTH - logoBoxW) + ((logoBoxW - w) / 2);
                    const y = (logoBoxH - h) / 2;

                    ctx.drawImage(img, x, y, w, h);
                    canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });
                };
                img.onerror = () => { canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); }); };
                img.src = logoUrl;
            } else {
                canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });
            }
        } catch (e) { resolve(null); }
    });
};

// Generate ONLY background image (photo + gradient, NO TEXT)
const generateFPTemp4BodyBgImage = async (proposal, bodyColor, footerColor) => {
async function generateFPTemp4Background({ proposal, bodyColor, footerColor }) {
    try {
        const WIDTH = 1600;
        const HEIGHT = 2090;

        const canvas = document.createElement('canvas');
        canvas.width = WIDTH;
        canvas.height = HEIGHT;

        const ctx = canvas.getContext('2d');

        // 1. Background
        ctx.fillStyle = bodyColor;
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        // 2. Project Photo (Cover)
        const firstProject = proposal?.projects?.[0];
        const photo = firstProject?.uploadedPhoto || firstProject?.photoURL;

        if (photo) {
            const pImg = await loadImageSafe(photo);
            if (pImg) {
                ctx.drawImage(pImg, 0, 0, WIDTH, HEIGHT);
            }
        }

        // 3. Footer Gradient
        const footerH = 550;
        const gradY = HEIGHT - footerH;

        const grad = ctx.createLinearGradient(0, HEIGHT, 0, gradY);
        grad.addColorStop(0, hexToRGBA(footerColor, 0.95));
        grad.addColorStop(0.5, hexToRGBA(footerColor, 0.7));
        grad.addColorStop(1, 'rgba(255, 255, 255, 0.3)');

        ctx.fillStyle = grad;
        ctx.fillRect(0, gradY, WIDTH, footerH);

        // 4. Convert to Uint8Array (await instead of callback)
        const blob = await new Promise(resolve => canvas.toBlob(resolve));
        if (!blob) return null;

        const buf = await blob.arrayBuffer();
        return new Uint8Array(buf);

    } catch (e) {
        console.error("FPTemp4 background error:", e);
        return null;
    }
}

// --- Helper ---
function loadImageSafe(src) {
    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);

        img.src = src;
    });
}


};

export const createFPTemp4 = async (proposal) => {
    const style = proposal.style || {};
    const colorScheme = style.colorScheme || {};
    const headerColor = colorScheme.header;
    const bodyColor = colorScheme.body;
    const footerColor = colorScheme.footer;

    const PAGE_HEIGHT = 16838;
    const HEADER_HEIGHT_DXA = 918;
    const SPACER_HEIGHT_DXA = 11000;  // Spacer for background
    const CONTENT_HEIGHT_DXA = PAGE_HEIGHT - HEADER_HEIGHT_DXA - SPACER_HEIGHT_DXA;

    // Helper for creating editable text
    const createText = (text, styleObj, defaultSize = 12, overrideColor = null) => {
        return new TextRun({
            text: text || "N/A",
            font: styleObj?.font || "Arial",
            size: (styleObj?.size || defaultSize) * SCALE,
            bold: styleObj?.bold,
            italics: styleObj?.italic,
            underline: styleObj?.underline,
            color: fixColor(overrideColor || styleObj?.color || "#FFFFFF")
        });
    };

    // 1. Header Image
    const headerImgData = await generateFPTemp4HeaderImage(headerColor, proposal.logo);
    let headerRun = null;
    if (headerImgData) {
        headerRun = new ImageRun({
            data: headerImgData,
            transformation: { width: FULL_WIDTH / 15, height: HEADER_HEIGHT_DXA / 15 }
        });
    }

    // 2. Body Background (floating)
    const bodyBgImgData = await generateFPTemp4BodyBgImage(proposal, bodyColor, footerColor);
    let bodyBgRun = null;
    if (bodyBgImgData) {
        bodyBgRun = new ImageRun({
            data: bodyBgImgData,
            transformation: { width: FULL_WIDTH / 15, height: (SPACER_HEIGHT_DXA + CONTENT_HEIGHT_DXA) / 15 },
            floating: {
                zIndex: 0,
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    align: HorizontalPositionAlign.CENTER,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PAGE,
                    align: VerticalPositionAlign.TOP,
                    offset: HEADER_HEIGHT_DXA,
                },
                behindDocument: true,
                wrap: { type: TextWrappingType.BEHIND }
            }
        });
    }

    const mainTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
        rows: [
            // Header Row
            new TableRow({
                height: { value: HEADER_HEIGHT_DXA, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        margins: { top: 0, bottom: 0, left: 0, right: 0 },
                        children: headerRun ? [new Paragraph({
                            spacing: { before: 0, after: 0 },
                            children: [headerRun]
                        })] : []
                    })
                ]
            }),
            // Spacer Row (holds floating background)
            new TableRow({
                height: { value: SPACER_HEIGHT_DXA, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        children: [
                            new Paragraph({
                                children: bodyBgRun ? [bodyBgRun] : []
                            })
                        ]
                    })
                ]
            }),
            // Content Row (EDITABLE TEXT on top of background)
            new TableRow({
                height: { value: CONTENT_HEIGHT_DXA, rule: HeightRule.AT_LEAST },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        verticalAlign: VerticalAlign.BOTTOM,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 200 },
                                children: [createText(proposal.name, style.heading, 32)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 400 },
                                children: [createText(`${proposal.address || 'N/A'}, ${proposal.city || 'N/A'}, ${proposal.stateProvince || 'N/A'}`, style.heading, 24)]
                            }),
                            // Submission info
                            new Table({
                                width: { size: 80, type: WidthType.PERCENTAGE },
                                alignment: AlignmentType.CENTER,
                                borders: { top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE }, left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE }, insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE } },
                                rows: [
                                    new TableRow({
                                        children: [
                                            new TableCell({
                                                width: { size: 50, type: WidthType.PERCENTAGE },
                                                margins: { top: 0, bottom: 600, left: 0, right: 0 },
                                                children: [
                                                    new Paragraph({
                                                        alignment: AlignmentType.CENTER,
                                                        spacing: { after: 150 },
                                                        children: [createText("SUBMISSION DATE", style.label, 16)]
                                                    }),
                                                    new Paragraph({
                                                        alignment: AlignmentType.CENTER,
                                                        children: [createText(proposal.submissionDate, style.text, 16)]
                                                    })
                                                ]
                                            }),
                                            new TableCell({
                                                width: { size: 50, type: WidthType.PERCENTAGE },
                                                margins: { top: 0, bottom: 600, left: 0, right: 0 },
                                                children: [
                                                    new Paragraph({
                                                        alignment: AlignmentType.CENTER,
                                                        spacing: { after: 150 },
                                                        children: [createText("SUBMITTED BY", style.label, 16)]
                                                    }),
                                                    new Paragraph({
                                                        alignment: AlignmentType.CENTER,
                                                        children: [createText(proposal.submittedBy, style.text, 16)]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });

    return {
        properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } },
        children: [mainTable]
    };
};
